<template>
    <div class="workflow-host">
        <BAlert
            v-if="runtimeError"
            variant="danger"
            show
            class="mb-1"
        >
            <div class="alert-body">
                {{ runtimeError.message || runtimeError }}
            </div>
        </BAlert>

        <component
            v-if="state"
            :is="rendererComponent"
            ref="renderer"
            :context="state.context"
            :steps="visibleSteps"
            :review-contributions="state.reviewContributions"
            :accordion="state.flow.settings.accordion"
            :renderer-settings="state.flow.settings.rendererSettings"
            :navigation-settings="state.flow.settings.navigation"
            @open-step="openStep"
            @close-step="closeStep"
            @validity-change="setStepValidity"
            @state-change="setStepState"
            @schedule-step-save="scheduleStepSave"
            @step-action="dispatchStepAction"
            @execute-terminal-step="executeTerminalStep"
            @complete-step="completeStep"
            @complete-external-step="completeExternalStep"
            @effect="publishEffect"
            @error="handleStepError"
        />
    </div>
</template>

<script>
import { BAlert } from 'bootstrap-vue'
import HorizontalStepperRenderer from './HorizontalStepperRenderer.vue'
import VerticalCollapseRenderer from './VerticalCollapseRenderer.vue'

const RENDERERS = Object.freeze({
    'horizontal-stepper': HorizontalStepperRenderer,
    'vertical-collapse': VerticalCollapseRenderer,
})

export default {
    name: 'WorkflowHost',
    components: {
        BAlert,
        HorizontalStepperRenderer,
        VerticalCollapseRenderer,
    },
    props: {
        runner: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            state: this.runner.getState(),
            runtimeError: null,
            scrollTimeout: null,
            unsubscribe: null,
        }
    },
    computed: {
        visibleSteps() {
            return this.state.steps.filter(step => step.isVisible && step.isRevealed)
        },
        rendererComponent() {
            return RENDERERS[this.state.flow.settings.renderer] || VerticalCollapseRenderer
        },
    },
    mounted() {
        this.unsubscribe = this.runner.subscribe(state => {
            this.state = state
        })
        this.runOperation(() => this.runner.initialize())
    },
    beforeDestroy() {
        clearTimeout(this.scrollTimeout)

        if (this.unsubscribe) {
            this.unsubscribe()
        }

        this.runner.dispose().catch(error => {
            this.runtimeError = error
        })
    },
    methods: {
        async runOperation(operation) {
            try {
                const result = await operation()

                this.$emit('operation-complete', result)

                return result
            } catch (error) {
                this.runtimeError = error
                this.$emit('runtime-error', error)

                return null
            }
        },
        async openStep(stepId) {
            const stepWasOpen = this.state.steps.some(step => step.id === stepId && step.isOpen)
            const result = await this.runOperation(() => this.runner.openStep(stepId))

            if (result === true && !stepWasOpen) {
                this.scrollAfterNavigation(stepId)
            }

            return result
        },
        closeStep(stepId) {
            return this.runOperation(() => this.runner.closeStep(stepId))
        },
        setStepValidity({ stepId, validation }) {
            this.runner.setStepValidity(stepId, validation)
        },
        setStepState({ stepId, partialState = {} }) {
            if (Object.prototype.hasOwnProperty.call(partialState, 'dirty')) {
                this.runner.setStepDirty(stepId, partialState.dirty)
            }

            if (Object.prototype.hasOwnProperty.call(partialState, 'loading')) {
                this.runner.setStepLoading(stepId, partialState.loading)
            }

            if (Object.prototype.hasOwnProperty.call(partialState, 'saving')) {
                this.runner.setStepSaving(stepId, partialState.saving)
            }
        },
        async completeStep(stepId) {
            const result = await this.runOperation(() => this.runner.completeStep(stepId))

            if (result && result.completed && result.nextStepId) {
                const legacyPolicy = this.state.flow.settings.scrollToNextStep
                    ? 'start'
                    : 'none'

                this.scrollAfterNavigation(result.nextStepId, legacyPolicy)
            }

            return result
        },
        scheduleStepSave(stepId) {
            return this.runOperation(() => this.runner.scheduleStepSave(stepId))
        },
        dispatchStepAction({ stepId, action }) {
            const step = this.state.steps.find(candidate => candidate.id === stepId)

            if (step && step.isMounted === false) {
                return Promise.resolve({ handled: false, reason: 'unmounted' })
            }

            return this.runOperation(() => this.runner.dispatchStepAction(stepId, action))
        },
        async executeTerminalStep(stepId) {
            const result = await this.runOperation(() => this.runner.executeTerminalStep(
                stepId,
                () => this.runner.dispatchStepAction(stepId, { type: 'terminal.execute' }),
            ))
            const terminalStep = this.state.steps.find(step => step.id === stepId)

            if (result && result.reason === 'invalid' && result.stepId) {
                const invalidBehavior = terminalStep
                    && terminalStep.completion
                    ? terminalStep.completion.invalidBehavior
                    : 'stay'

                if (invalidBehavior === 'open-first-invalid') {
                    await this.runOperation(() => this.runner.openStep(result.stepId))
                    this.scrollToStep(result.stepId)
                } else if (invalidBehavior === 'review-summary') {
                    this.scrollToStep(stepId)
                }
            } else if (
                result
                && terminalStep
                && terminalStep.completion
                && terminalStep.completion.scrollAfterExecution === true
                && (
                    result.executed === true
                    || ['terminal-error', 'flush-error'].includes(result.reason)
                )
            ) {
                this.scrollToStep(stepId)
            }

            return result
        },
        completeExternalStep({ stepId, validation }) {
            return this.runOperation(() => this.runner.completeExternalStep(stepId, validation))
        },
        publishEffect(effect) {
            return this.runOperation(() => this.runner.publishEffect(effect))
        },
        handleStepError({ error }) {
            this.runtimeError = error
            this.$emit('runtime-error', error)
        },
        navigationScrollPolicy(stepId, fallbackPolicy = 'none') {
            const step = this.state.steps.find(candidate => candidate.id === stepId)
            const stepNavigation = step && step.navigation

            if (stepNavigation && typeof stepNavigation.scrollOnOpen === 'string') {
                return stepNavigation.scrollOnOpen
            }

            const rawSettings = this.state.flow.manifest && this.state.flow.manifest.settings
            const flowNavigation = rawSettings && rawSettings.navigation

            if (flowNavigation && typeof flowNavigation.scrollOnOpen === 'string') {
                return flowNavigation.scrollOnOpen
            }

            return fallbackPolicy
        },
        scrollAfterNavigation(stepId, fallbackPolicy = 'none') {
            const policy = this.navigationScrollPolicy(stepId, fallbackPolicy)

            if (policy !== 'none') {
                this.scrollToStep(stepId, policy)
            }
        },
        scrollToStep(stepId, policy = 'start') {
            this.$nextTick(() => {
                clearTimeout(this.scrollTimeout)
                this.scrollTimeout = setTimeout(() => {
                    const stepElement = Array.from(
                        this.$el.querySelectorAll('[data-workflow-step-id]'),
                    ).find(element => element.getAttribute('data-workflow-step-id') === stepId)

                    if (!stepElement) {
                        return
                    }

                    const horizontalRenderer = stepElement.closest(
                        '.horizontal-stepper-renderer',
                    )
                    const target = horizontalRenderer
                        ? horizontalRenderer.querySelector(
                            '.horizontal-stepper-renderer__navigation',
                        ) || stepElement
                        : stepElement

                    if (policy === 'if-needed') {
                        const bounds = target.getBoundingClientRect()
                        const viewportHeight = window.innerHeight
                            || document.documentElement.clientHeight

                        if (bounds.top >= 0 && bounds.bottom <= viewportHeight) {
                            return
                        }
                    }

                    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, this.state.flow.settings.nextStepScrollDelayMs || 0)
            })
        },
    },
}
</script>
